
import {customElement, LitElement, property, html as _, TemplateResult} from "lit-element";

import {NAME_SPACE} from "@kta20-core/constants";
import {FilterItem} from "@kta20-ui-base/comps/models/filter-item";
import emptyListTmpl from '@kta20-ui-base/comps/sharedTemplates/empty-list';
import {IValueObject} from "@kta20-ui-base/comps/interfaces/i-value-object";
import {DefaultListItemAdapter} from "@kta20-ui-base/comps/adapter/default-list-item-adapter";
import {IUiSearchFilterComponent} from "@kta20-ui-base/comps/interfaces/i-ui-search-filter-component";


export enum SortingTypes {
    STRING = 'string',
    NUMBER_OR_DATE = 'number-date',
}

export enum SortingModes {
    ASC = 1,
    DESC = 2
}

export enum StatusItem {
    CLOSED = 1,
    OPENED
}

const defaultItemTmpl=(uiListComp:UiListComponent,item:any)=>_`
    <ion-item button detail="${uiListComp.enableItemArrow}" @click="${(ev:Event)=>{uiListComp.onItemClick(ev,item)}}">
        <ion-label>
            <p>${uiListComp.itemAdapter.getHeader(item)}</p>
            <ion-text color="primary">
                <h1>${uiListComp.itemAdapter.getTitle(item)}</h1>
            </ion-text>
            <ion-note>${uiListComp.itemAdapter.getText(item)}</ion-note>
            <ion-text data-kta20-item-extra class="ion-hide" color="dark" size="sm">
                <p style="margin-top: 18px">${uiListComp.itemAdapter.getExtra(item)}</p>
            </ion-text>
        </ion-label>
    </ion-item>
`;

const defaultItemHeaderTmpl=(uiListComp:UiListComponent,item:any,index:number)=>_`
    <ion-list-header color="${uiListComp.listHeaderColor}" data-index="${index}">
        <ion-label>${IValueObject.isImplementedBy(item[uiListComp.sortBy]) ? (<IValueObject>item[uiListComp.sortBy].getValue()) : item[uiListComp.sortBy]}</ion-label>
    </ion-list-header>
`;

const leftSwipeTmpl=(uiListComp:UiListComponent, item:any)=>_`
        <ion-item-options side="start">
          <ion-item-option button disabled="${!uiListComp.itemAdapter.isSwipeLeftEnabled(item)}" color="${uiListComp.itemAdapter.isSwipeLeftEnabled(item) ? uiListComp.itemSwipeLeftColor : 'medium'}" @click="${()=>{uiListComp.onSwipeCommandClick('r', item)}}">
            <ion-icon size="large" name="${uiListComp.itemSwipeLeftIconName}" src="${uiListComp.itemSwipeLeftIconSrc}"></ion-icon>
            ${uiListComp.itemSwipeLeftText}
          </ion-item-option>
        </ion-item-options>
`;
const rightSwipeTmpl=(uiListComp:UiListComponent, item:any)=>_`
        <ion-item-options side="end">
          <ion-item-option button disabled="${!uiListComp.itemAdapter.isSwipeRightEnabled(item)}" color="${uiListComp.itemAdapter.isSwipeRightEnabled(item) ? uiListComp.itemSwipeRightColor : 'medium'}" @click="${()=>{uiListComp.onSwipeCommandClick('l', item)}}">
            <ion-icon size="large" name="${uiListComp.itemSwipeRightIconName}" src="${uiListComp.itemSwipeRightIconSrc}"></ion-icon>
                ${uiListComp.itemSwipeRightText}
          </ion-item-option>
        </ion-item-options>
`;

class FullSwipeItemTmpl {
    constructor(
        public leftSwipeTmpl:(uiListComp:UiListComponent, item)=>TemplateResult,
        public rightSwipeTmpl:(uiListComp:UiListComponent, item)=>TemplateResult
    ){
        this.leftSwipeTmpl = this.leftSwipeTmpl ? this.leftSwipeTmpl : ()=>_``;
        this.rightSwipeTmpl = this.rightSwipeTmpl ? this.rightSwipeTmpl : ()=>_``;
    }

    render(uiListComp:UiListComponent,item:any):TemplateResult {
        return _`
            <ion-item-sliding>
        
                ${this.leftSwipeTmpl(uiListComp, item)}
                
                ${uiListComp.itemTemplate(uiListComp, item)}
                
                ${this.rightSwipeTmpl(uiListComp, item)}
        
            </ion-item-sliding>
        `;
    }
}

@customElement(UiListComponent.TAG_NAME)
export class UiListComponent extends LitElement {

    static TAG_NAME = 'kta20-ui-list';

    static Events = {
        PULL_TO_REFRESH:`kta20OnPullToRefresh`,
        SWIPE_LEFT_COMMAND_CLICKED: `kta20OnSwipeLeftClicked`,
        SWIPE_RIGHT_COMMAND_CLICKED: `kta20OnSwipeRightClicked`,
        ON_ITEM_CLICKED:`kta20OnItemClicked`,
        SCROLL:`kta20OnScroll`,
        SCROLL_START:`kta20OnScrollStart`,
        SCROLL_END:`kta20OnScrollEnd`,
        REDUCER_VALUE_CHANGED:`kta20OnReducerValueChanged`
    };

    //TODO implement sort direction
    private static SortingTypeMap = {
        [SortingTypes.STRING]:(a,b,sortField:string,sortMode:SortingModes)=>{
            if(IValueObject.isImplementedBy(a[sortField])) {
                if(sortMode === SortingModes.DESC) return (<IValueObject>a[sortField]).getValue().toString().toLowerCase() < (<IValueObject>b[sortField]).getValue().toString().toLowerCase() ? -1 : 1;
                else return (<IValueObject>a[sortField]).getValue().toString().toLowerCase() > (<IValueObject>b[sortField]).getValue().toString().toLowerCase() ? -1 : 1;
            }
            else {
                if(sortMode === SortingModes.DESC) return a[sortField].toString().toLowerCase() < b[sortField].toString().toLowerCase() ? -1 : 1;
                else return a[sortField].toString().toLowerCase() > b[sortField].toString().toLowerCase() ? -1 : 1;
            }

        },
        [SortingTypes.NUMBER_OR_DATE]:(a,b,sortField:string,sortMode:SortingModes)=>{
            if(IValueObject.isImplementedBy(a[sortField])) {
                if(sortMode === SortingModes.DESC) return <any>(<IValueObject>a[sortField]).getValue() - <any>(<IValueObject>b[sortField]).getValue()
                else return <any>(<IValueObject>b[sortField]).getValue() - <any>(<IValueObject>a[sortField]).getValue()
            }

            else {
                if(sortMode === SortingModes.DESC) return b[sortField] - a[sortField];
                else return a[sortField] - b[sortField];
            }

        }
    };

    private cachedEnableGroups:boolean=undefined;
    private itemsToDisplayAmount:number=0;
    private fullSwipeItemTmpl:FullSwipeItemTmpl;
    protected searchListener;

    @property({ type: String })
    private searchModelProp:string;

    @property({ type: String })
    private searchValue:string;

    @property({ type: Boolean })
    enableScrollEvents:boolean=false; //disabled by default for performance reasons

    @property({ type: Boolean })
    enableItemArrow:boolean=true;

    @property({ type: String})
    reducerLabel:string='[reduce-to]';

    @property({ type: String})
    reducerResetLabel:string='[reset]';

    @property({ type: Array})
    reducerItems:FilterItem[]=[]; // key (model field name, field localized label)

    @property()
    reducerValue:any;

    @property({ type: String})
    reducerModelProp:string;

    /**
     * Note: in Ionic the interface accepts
     * configuration (InterfaceOption). Here
     * we are ignoring it too keep things easy
     */
    @property({ type: String })
    reducerInterface:string='popover';

    /**
     * Disabled by default since
     * rendering items with groups
     * is more expensive.
     *
     * Works in tandem with "sortBy"
     * since groups are rendered
     * according to the sorting
     * field values
     */
    @property({ type: Boolean })
    enableGroups:boolean=false;

    @property({ type: String })
    sortBy:string;

    @property({ type: SortingTypes })
    sortingType:SortingTypes;

    /**
     * ASC by default
     */
    @property({ type: SortingModes })
    sortingMode:SortingModes;

    @property({ type: Boolean })
    enableTotal:boolean=true;

    @property({ type: Function })
    itemTemplate:(uiListComp:UiListComponent ,item:DefaultListItemAdapter)=>TemplateResult = defaultItemTmpl.bind(this);

    @property({ type: Function })
    itemHeaderTemplate:(uiListComp:UiListComponent ,item:any,index:number)=>TemplateResult = defaultItemHeaderTmpl.bind(this);

    @property({ type: String })
    listHeaderColor:string='light';

    @property({ type: String })
    itemSwipeLeftText:string='';

    @property({ type: String })
    itemSwipeLeftIconName:string='';

    @property({ type: String })
    itemSwipeLeftIconSrc:string='';

    @property({ type: String })
    itemSwipeLeftColor:string='warning';

    @property({ type: String })
    itemSwipeRightText:string='';

    @property({ type: String })
    itemSwipeRightIconName:string='';

    @property({ type: String })
    itemSwipeRightIconSrc:string='';

    @property({ type: String })
    itemSwipeRightColor:string = 'secondary';

    @property({ type: Boolean })
    enablePullToRefresh:boolean=true;

    @property({ type: String })
    pullingLabel:string='[release-to-refresh]';

    /**
     * All items must by of the same
     * class type.
     */

    private _itemsData:any[]=[];

    @property({ type: Array })
    get itemsData():any[]{
        return this._itemsData;
    }

    set itemsData(value:any[]){
        let oldVal = this._itemsData;
        this._itemsData = value || [];

        this.configureReducer();
        this.composeItemTemplate();

        this.requestUpdate('itemsData', oldVal);
    }


    @property({ type: DefaultListItemAdapter })
    itemAdapter:DefaultListItemAdapter;

    @property({ type: String })
    noItemsLabel:string;

    @property({type: Object})
    searchBarSlot:Object;

    readonly renderRoot:Element | ShadowRoot;

    constructor(useShadow:boolean=false){
        super();
        this.renderRoot = useShadow ? this.shadowRoot : this;
    }

    async connectedCallback() {
        super.connectedCallback();
        //TODO: need to invoke on changes to props too? to check it...
        this.configureReducer();
        this.composeItemTemplate();

        await this.updateComplete;

        this.configureSearchBar(true);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.configureSearchBar(false);
    }

    configureReducer() {
        if (this.reducerModelProp) {
            let worksWithValueObjects:boolean;

            const values = this._itemsData.map(item => item[this.reducerModelProp]);
            if(values.length>0){

                worksWithValueObjects = IValueObject.isImplementedBy(values[0]);

                const filterResetValue = worksWithValueObjects ? {
                    getValue:()=>{ return '**'}
                } : ''

                this.reducerItems = [new FilterItem(filterResetValue, this.reducerResetLabel)];

                if(worksWithValueObjects)
                    values.forEach(v => {
                        const label = (<IValueObject>v).getLabel();
                        const filterItemCandidate = new FilterItem(v,  label);
                        if (!this.reducerItems.find((item)=>
                            (<IValueObject>item.value).getValue().toString().toLowerCase() === (<IValueObject>filterItemCandidate.value).getValue().toString().toLowerCase()
                        ))
                            this.reducerItems.push(filterItemCandidate);

                    });
                else
                    values.forEach(v => {
                        const filterItemCandidate = new FilterItem(v,  `${this.reducerLabel} ${v}`);
                        if (!this.reducerItems.find(item =>
                            item.value.toString().toLowerCase() ===filterItemCandidate.value.toString().toLowerCase())
                        )
                            this.reducerItems.push(filterItemCandidate);
                    });
            }
        }
    }

    composeItemTemplate(){
        this.itemSwipeLeftColor = this.itemSwipeLeftColor || 'warning';
        this.itemSwipeLeftIconSrc = this.itemSwipeLeftIconSrc || '';
        this.itemSwipeLeftIconName = this.itemSwipeLeftIconName || '';

        this.itemSwipeRightColor = this.itemSwipeRightColor || 'secondary';
        this.itemSwipeRightIconSrc = this.itemSwipeRightIconSrc || '';
        this.itemSwipeRightIconName = this.itemSwipeRightIconName || '';

        const hasLeftSwipe = (this.itemSwipeLeftColor.length>0 && (this.itemSwipeLeftIconSrc.length>0 || this.itemSwipeLeftIconName.length>0));
        const hasRightSwipe = (this.itemSwipeRightColor && (this.itemSwipeRightIconSrc || this.itemSwipeRightIconName));

        this.fullSwipeItemTmpl = new FullSwipeItemTmpl(
            hasLeftSwipe ? leftSwipeTmpl.bind(this) : null,
            hasRightSwipe ? rightSwipeTmpl.bind(this) : null
        );
    }

    configureSearchBar(listener:boolean) {
        const searchBar = this.getSearchBar();
        if(searchBar){
            this.searchListener = this.queryBySearchBar.bind(this);
            listener ?
                searchBar.addEventListener(IUiSearchFilterComponent.APPLY_FILTER_COMMAND, this.searchListener) :
                searchBar.removeEventListener(IUiSearchFilterComponent.APPLY_FILTER_COMMAND,this.searchListener);
        }
    }

    onItemClick(ev:Event,item:DefaultListItemAdapter){
        //@ts-ignore
        this.getList().closeSlidingItems();

        const status = this.toggleItem(<HTMLElement>ev.currentTarget);
        this.dispatchEvent(new CustomEvent(UiListComponent.Events.ON_ITEM_CLICKED, {detail:[item, status], bubbles:true,cancelable:true,composed:true}));
        console.log(`${NAME_SPACE}: event ${UiListComponent.Events.ON_ITEM_CLICKED} fired with item ${item}`);
    }

    /**
     * @param side {string} : r|l
     */
    onSwipeCommandClick(side:string, item:any){
        //@ts-ignore
        this.getList().closeSlidingItems();

        const sideToEventsMap = {
            l:UiListComponent.Events.SWIPE_LEFT_COMMAND_CLICKED,
            r:UiListComponent.Events.SWIPE_RIGHT_COMMAND_CLICKED
        }
        this.dispatchEvent(new CustomEvent(sideToEventsMap[side], {detail:item, bubbles:true,cancelable:true,composed:true}));
        console.log(`${NAME_SPACE}: event ${sideToEventsMap[side]} fired`);
    }

    protected createRenderRoot(): Element | ShadowRoot {
        return this.renderRoot;
    }

    protected onPullToRefresh(event:Event){
        (<any>event.currentTarget).cancel(); //restores ion-refresher to initial state
        this.dispatchEvent(new CustomEvent(UiListComponent.Events.PULL_TO_REFRESH, {detail:this, bubbles:true, composed:true,cancelable:true}));
        console.log(`${NAME_SPACE}: event ${UiListComponent.Events.PULL_TO_REFRESH} fired`);
    }

    protected onAnyScroll(scrollEventName:string){
        this.dispatchEvent(new CustomEvent(scrollEventName, {detail:this, bubbles:true, composed:true,cancelable:true}));
        console.log(`${NAME_SPACE}: event ${scrollEventName} fired`);
    }

    protected onReducerValueChanged(value:any){
        const rawValue = IValueObject.isImplementedBy(value) ? (<IValueObject>value).getValue() : value;

        this.reducerValue = rawValue!=='**' ? value : null;

        if(value){
            this.cachedEnableGroups = this.cachedEnableGroups || this.enableGroups;
            this.enableGroups = false;
        }
        else
            this.enableGroups = this.cachedEnableGroups;

        this.dispatchEvent(new CustomEvent(UiListComponent.Events.REDUCER_VALUE_CHANGED, {detail:this.reducerValue, bubbles:true, composed:true,cancelable:true}));

        console.log(`${NAME_SPACE}: event ${UiListComponent.Events.REDUCER_VALUE_CHANGED} fired with value "${this.reducerValue}"`);
    }

    /**
     * Sorts and reduces (if enabled) otherwise returns
     * input array as is.
     * @param items
     */
    protected prepareItems(items:any[]):any[] {
        items = this.applySorting(items);
        items = this.reduce(items);
        items = this.applySearch(items);

        this.itemsToDisplayAmount = items.length;

        return items;
    }

    protected applySearch(items:any[]):any[]{
        if(this.searchModelProp && this.searchValue!=null && this.searchValue!==''){

            const filteredItemsSet = new Set();

            let valueType = (typeof (<any>this.searchValue).getMonth === 'function') ? 'date' : typeof this.searchValue;
            //@ts-ignore
            valueType = IValueObject.isImplementedBy(items[0][this.searchModelProp]) ? IValueObject.name : valueType;

            const typeBasedComparatorMap = {
                //comparator for properties of type String (contains logic)
                string:(a:string,b:string):boolean=>{
                    return a.toLowerCase().indexOf(b.toLowerCase())!==-1;
                },
                //comparator for properties of type Number (equality logic)
                number:(a:number,b:number):boolean=>{
                    return a===b;
                },
                //comparator for properties of type Boolean (equality logic)
                boolean:(a:boolean,b:boolean):boolean=>{
                    return a===b;
                },
                //comparator for properties of type Date (equality logic, excluding time)
                date:(a:Date,b:Date):boolean=>{
                    a.setHours(0,0,0,0);
                    b.setHours(0,0,0,0);
                    return a.getTime()===b.getTime();
                },
                object:(a:Object,b:Object):boolean=>{
                    return a===b;
                },
                [IValueObject.name]:(a:IValueObject,b:any):boolean=>{
                    return a.getValue()===b;
                }
            };

            const comparator:(a:any,b:any)=>boolean = typeBasedComparatorMap[valueType];

            if(!comparator)
                throw new Error('Cannot map the item property value to any of known types, cannot select a comparator.');

            //lookup for search input value into a single
            //model prop or all of them assuming that all
            //objects in array have the same type (model structure)
            const propsLookUp = this.searchModelProp ? [this.searchModelProp] : Object.keys(items[0]);

            //Applies the filter:
            //props may have of-course different data types,
            //here we decided to lookup at the values as string
            //since the user provides a single string input anyway.
            //Filter is case insensitive and looks if the value
            //is available anywhere in the property (contains logic)
            propsLookUp.forEach(prop=>{
                const queryResults = items.filter(item=>comparator(item[prop],this.searchValue));
                queryResults.forEach(item=>filteredItemsSet.add(item)); //filteredItems is a <Set>, so keeps unique values only
            });

            items = Array.from(filteredItemsSet);
        }

        return items;
    }

    protected reduce(items: any[]) {
        if (this.reducerModelProp && this.reducerValue) {
            if (IValueObject.isImplementedBy(this.reducerValue)) {
                const rawValue = (<IValueObject>this.reducerValue).getValue();
                items = items.filter(item => (<IValueObject>item[this.reducerModelProp]).getValue().toString().toLowerCase() === rawValue.toString().toLowerCase());
            } else
                items = items.filter(item => item[this.reducerModelProp].toString().toLowerCase() === this.reducerValue.toString().toLowerCase());
        }
        return items;
    }

    protected applySorting(items: any[]) {
        if (this.sortBy && this.sortingType && items.length > 0)
            items = items.sort(
                (a, b) => UiListComponent.SortingTypeMap[this.sortingType](a, b, this.sortBy, this.sortingMode)
            );
        return items;
    }

    protected queryBySearchBar(ev:CustomEvent){
        if(this._itemsData && this._itemsData.length>0 && this._itemsData[0][ev.detail.fieldName]){
            //start the search..
            this.searchModelProp = ev.detail.fieldName;
            this.searchValue = ev.detail.inputValue;
        }
        else throw Error(`The property "${ev.detail.fieldName}" is not recognised in the item model class, or "itemsData" is not available.`);
    }

    protected getSearchBar():HTMLElement{
        return this.renderRoot.querySelector('#searchBarSlot > *');
    }

    protected getList():HTMLElement{
        return this.renderRoot.querySelector('ion-list');
    }

    /**
     * Items in the list can be grouped
     * by the value of the field they are
     * sorted on (if any).
     * This function invoked during template
     * render of the items checks if the
     * current item must be printed inside
     * a list header together with other
     * items or if a new group is needed
     * instead. Every time the value in
     * sorting field changes you a new group
     * is needed.
     * @param item
     * @param index
     */
    protected isNewGroup(item: DefaultListItemAdapter, index:number):boolean {
        if(index===0) return true;

        if(IValueObject.isImplementedBy(item[this.sortBy]))
            return (<IValueObject>item[this.sortBy]).getValue() != (<IValueObject>this._itemsData[index-1][this.sortBy]).getValue()
        else
            return item[this.sortBy].toString() != this._itemsData[index-1][this.sortBy].toString();
    }

    protected toggleItem(itemElem:HTMLElement){
        const labelElem = itemElem.querySelector('ion-label');
        const itemExtraElem = labelElem.querySelector('[data-kta20-item-extra]');

        labelElem.className = labelElem.className.indexOf('ion-text-wrap')===-1 ? `${labelElem.className} ion-text-wrap` : labelElem.className.replace('ion-text-wrap','');
        itemExtraElem.className = itemExtraElem.className.indexOf('ion-hide')===-1 ? `${itemExtraElem.className} ion-hide` : itemExtraElem.className.replace('ion-hide','');

        itemElem.scrollIntoView({behavior:"smooth"});

        return itemExtraElem.className.indexOf('ion-hide')===-1 ? StatusItem.CLOSED : StatusItem.OPENED;
    }

    protected render(){ return _`
        
        <!-- 
            Wrapping with ion-page is needed 
            to make the ion-content to work properly.
            If the consumer view doesn't want to display
            this list in "full view" it can wrap or style
            this wco to make it smaller
        -->

        <div class="ion-page">
        
            <div id="searchBarSlot">${this.searchBarSlot}</div>

            <ion-content id="list-content"
                scroll-events="${this.enableScrollEvents}"
                @ionScrollStart="${()=>{this.onAnyScroll(UiListComponent.Events.SCROLL_START)}}"
                @ionScroll="${()=>{this.onAnyScroll(UiListComponent.Events.SCROLL)}}"
                @ionScrollEnd="${()=>{this.onAnyScroll(UiListComponent.Events.SCROLL_END)}}">

                 ${this.enablePullToRefresh ? _`
                     <ion-refresher slot="fixed" @ionRefresh="${(event)=>{this.onPullToRefresh(event)}}">
                        <ion-refresher-content
                          pulling-icon="arrow-dropdown"
                          refreshing-spinner="dots"
                          pulling-text="${this.pullingLabel}">
                        </ion-refresher-content>
                    </ion-refresher>
                ` : ``}
                
                ${this.itemsData.length>0 ? _`
                    <ion-list>
                        ${(this.enableGroups && this.sortBy && this.sortingType) ? _`
                            ${this.prepareItems(this.itemsData).map((item, index)=>_`
                                ${this.isNewGroup(item, index) ? _`${this.itemHeaderTemplate(this,item,index)}` : ``}
                               
                                ${this.fullSwipeItemTmpl.render(this,item)}
                            `)}
                        ` : _`
                            ${this.prepareItems(this.itemsData).map(item=>this.fullSwipeItemTmpl.render(this,item))}
                        `}
                    </ion-list>
                ` : _`${emptyListTmpl(this.noItemsLabel)}`
                }
             </ion-content>
             
             ${(this.reducerItems || this.enableTotal) ? _`
                <ion-footer>
                  <ion-toolbar>
                    <ion-grid>
                        <ion-row>
                            <ion-col align-self-center>
                                ${this.enableTotal ? this.itemsToDisplayAmount : ''}
                            </ion-col>
                            <ion-col align-self-center>
                                <ion-select 
                                        disabled="${this.reducerItems.length<3}" 
                                        interface="${this.reducerInterface}" 
                                        placeHolder="${this.reducerLabel}"
                                        .value="${this.reducerValue}"
                                        @ionChange="${(event:CustomEvent)=>{this.onReducerValueChanged(event.detail.value)}}">
                                        ${this.reducerItems.map(item=>_`
                                        
                                            <ion-select-option .value="${item.value}">${item.label}</ion-select-option>
                                            
                                        `)}
                              </ion-select>
                            </ion-col>
                        </ion-row>
                    </ion-grid>
                  </ion-toolbar>
                </ion-footer>
             ` : ``}
             
        </div>
    `;}
}